export const MAJOR_AIRPORTS = [
  { code: "KBP", name: "Boryspil Intl", city: "Kyiv", country: "Ukraine", lat: 50.345, lng: 30.895 },
  { code: "TLV", name: "Ben Gurion", city: "Tel Aviv", country: "Israel", lat: 32.011, lng: 34.887 },
  { code: "BGW", name: "Baghdad Intl", city: "Baghdad", country: "Iraq", lat: 33.263, lng: 44.236 },
  { code: "AMM", name: "Queen Alia", city: "Amman", country: "Jordan", lat: 31.723, lng: 35.993 },
  { code: "BEY", name: "Rafic Hariri", city: "Beirut", country: "Lebanon", lat: 33.821, lng: 35.488 },
  { code: "DAM", name: "Damascus Intl", city: "Damascus", country: "Syria", lat: 33.411, lng: 36.515 },
  { code: "IKA", name: "Imam Khomeini", city: "Tehran", country: "Iran", lat: 35.416, lng: 51.152 },
  { code: "KHI", name: "Jinnah Intl", city: "Karachi", country: "Pakistan", lat: 24.907, lng: 67.161 },
  { code: "KRT", name: "Khartoum Intl", city: "Khartoum", country: "Sudan", lat: 15.590, lng: 32.553 },
  { code: "ADD", name: "Bole Intl", city: "Addis Ababa", country: "Ethiopia", lat: 8.978, lng: 38.799 },
  { code: "MGQ", name: "Aden Abdulle", city: "Mogadishu", country: "Somalia", lat: 2.014, lng: 45.305 },
  { code: "KBL", name: "Hamid Karzai", city: "Kabul", country: "Afghanistan", lat: 34.566, lng: 69.213 },
  { code: "RGN", name: "Yangon Intl", city: "Yangon", country: "Myanmar", lat: 16.907, lng: 96.133 },
  { code: "ODS", name: "Odesa Intl", city: "Odesa", country: "Ukraine", lat: 46.427, lng: 30.676 },
  { code: "LWO", name: "Lviv Intl", city: "Lviv", country: "Ukraine", lat: 49.813, lng: 23.956 },
  { code: "MHD", name: "Mashhad Intl", city: "Mashhad", country: "Iran", lat: 36.236, lng: 59.641 },
  { code: "ISF", name: "Isfahan Intl", city: "Isfahan", country: "Iran", lat: 32.751, lng: 51.862 },
  { code: "SYZ", name: "Shiraz Intl", city: "Shiraz", country: "Iran", lat: 29.539, lng: 52.590 },
  { code: "DOH", name: "Hamad Intl", city: "Doha", country: "Qatar", lat: 25.261, lng: 51.565 },
  { code: "DXB", name: "Dubai Intl", city: "Dubai", country: "UAE", lat: 25.253, lng: 55.365 },
  { code: "AUH", name: "Abu Dhabi Intl", city: "Abu Dhabi", country: "UAE", lat: 24.433, lng: 54.651 },
  { code: "SHJ", name: "Sharjah Intl", city: "Sharjah", country: "UAE", lat: 25.329, lng: 55.517 },
  { code: "BAH", name: "Bahrain Intl", city: "Manama", country: "Bahrain", lat: 26.271, lng: 50.634 },
  { code: "RUH", name: "King Khalid", city: "Riyadh", country: "Saudi Arabia", lat: 24.958, lng: 46.699 },
  { code: "JED", name: "King Abdulaziz", city: "Jeddah", country: "Saudi Arabia", lat: 21.679, lng: 39.157 },
  { code: "DMM", name: "King Fahd", city: "Dammam", country: "Saudi Arabia", lat: 26.471, lng: 49.798 },
  { code: "KWI", name: "Kuwait Intl", city: "Kuwait City", country: "Kuwait", lat: 29.226, lng: 47.969 },
  { code: "AQJ", name: "King Hussein", city: "Aqaba", country: "Jordan", lat: 29.612, lng: 35.018 },
];

export const EVENT_WEIGHTS: Record<string, number> = {
  missile: 4,
  air_raid: 3,
  drone: 2.5,
  explosion: 2,
  movement: 0.5,
};

export function haversine(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function getAirspaceClosureProbability(risk: number, missileCount: number): number {
  return Math.round(Math.min(95, risk * 15 + missileCount * 8));
}

export function getAirportStatus(risk: number, closureProb: number): { label: string; color: string } {
  if (closureProb >= 70) return { label: "LIKELY CLOSED", color: "hsl(var(--destructive))" };
  if (closureProb >= 40) return { label: "AT RISK", color: "hsl(var(--warning))" };
  return { label: "OPEN", color: "hsl(var(--success))" };
}

export interface AirportRisk {
  code: string;
  name: string;
  city: string;
  country: string;
  lat: number;
  lng: number;
  risk: number;
  count: number;
  missileCount: number;
  closureProb: number;
  status: { label: string; color: string };
}

export function calculateAirportRisks(
  events: Array<{ lat: number | null; lng: number | null; event_type: string; occurred_at: string }> | undefined
): AirportRisk[] {
  const RADIUS = 200;

  if (!events?.length) {
    return MAJOR_AIRPORTS.map((a) => ({
      ...a,
      risk: 0,
      count: 0,
      missileCount: 0,
      closureProb: 0,
      status: getAirportStatus(0, 0),
    }));
  }

  return MAJOR_AIRPORTS.map((airport) => {
    let risk = 0;
    let count = 0;
    let missileCount = 0;
    const now = Date.now();

    events.forEach((e) => {
      if (!e.lat || !e.lng) return;
      const dist = haversine(airport.lat, airport.lng, e.lat, e.lng);
      if (dist > RADIUS) return;
      count++;
      if (e.event_type === "missile" || e.event_type === "air_raid") missileCount++;
      const weight = EVENT_WEIGHTS[e.event_type] || 1;
      const hoursAgo = (now - new Date(e.occurred_at).getTime()) / 3600000;
      const recency = hoursAgo < 6 ? 2 : hoursAgo < 24 ? 1.5 : 1;
      const proximity = 1 - dist / RADIUS;
      risk += weight * recency * proximity;
    });

    risk = Math.min(5, risk / Math.max(1, count / 2));
    const closureProb = getAirspaceClosureProbability(risk, missileCount);
    const status = getAirportStatus(risk, closureProb);
    return {
      ...airport,
      risk: parseFloat(risk.toFixed(1)),
      count,
      missileCount,
      closureProb,
      status,
    };
  }).sort((a, b) => b.risk - a.risk);
}
